import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import {
  addDoc,
  collection,
  Firestore,
  serverTimestamp,
} from '@angular/fire/firestore';

import { AuthService } from '@app/core/services/auth.service';
import { SnackbarService } from '@app/core/services/snackbar.service';

interface ProductBuilderFormData {
  name: string;
  email: string;
  projectDescription: string;
  budgetRange: string;
  timeline: string;
}

@Component({
  selector: 'app-product-builder',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './product-builder.html',
  styleUrl: './product-builder.css',
})
export class ProductBuilderComponent implements OnInit {
  form: ProductBuilderFormData = {
    name: '',
    email: '',
    projectDescription: '',
    budgetRange: '',
    timeline: '',
  };

  budgetOptions = [
    'Not sure yet',
    'Under ₹1,00,000',
    '₹1,00,000 – ₹5,00,000',
    '₹5,00,000 – ₹15,00,000',
    '₹15,00,000+',
  ];

  timelineOptions = ['Not sure yet', 'ASAP', 'Within a month', '1–3 months', '3+ months'];

  submitting = false;
  submitted = false;
  errorMessage = '';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private firestore: Firestore,
    public auth: AuthService,
    private snack: SnackbarService,
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
    const user = this.auth.user.value;
    if (user) {
      this.form.name = user.displayName || '';
      this.form.email = user.email || '';
    }
  }

  scrollToForm() {
    if (isPlatformBrowser(this.platformId)) {
      const el = document.getElementById('product-builder-form');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  scrollToHowItWorks() {
    if (isPlatformBrowser(this.platformId)) {
      const el = document.getElementById('how-it-works');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async submitRequest() {
    this.errorMessage = '';

    if (!this.form.name.trim()) {
      this.errorMessage = 'Please enter your name.';
      return;
    }
    if (!this.isValidEmail(this.form.email.trim())) {
      this.errorMessage = 'Please enter a valid email address.';
      return;
    }
    if (!this.form.projectDescription.trim()) {
      this.errorMessage = 'Tell us a bit about what you want to build.';
      return;
    }

    this.submitting = true;

    try {
      const user = this.auth.user.value;
      await addDoc(collection(this.firestore, 'product_builder_requests'), {
        name: this.form.name.trim(),
        email: this.form.email.trim(),
        projectDescription: this.form.projectDescription.trim(),
        budgetRange: this.form.budgetRange || 'Not specified',
        timeline: this.form.timeline || 'Not specified',
        userId: user?.uid || null,
        status: 'new',
        createdAt: serverTimestamp(),
      });

      this.submitted = true;
    } catch (err) {
      console.error('[ProductBuilder] submission failed:', err);
      this.snack.showError('Something went wrong sending your request. Please try again.');
    } finally {
      this.submitting = false;
    }
  }

  submitAnother() {
    this.submitted = false;
    this.form = {
      name: this.auth.user.value?.displayName || '',
      email: this.auth.user.value?.email || '',
      projectDescription: '',
      budgetRange: '',
      timeline: '',
    };
  }
}